const FOCUSABLE_ELEMENTS = [
  "a[href]",
  "area[href]",
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  "select:not([disabled]):not([aria-hidden])",
  "textarea:not([disabled]):not([aria-hidden])",
  "button:not([disabled]):not([aria-hidden])",
  "iframe",
  "object",
  "embed",
  "[contenteditable]",
  '[tabindex]:not([tabindex^="-"])',
];

export function findAncestor(el, selector) {
  while ((el = el.parentElement) && !el.matches(selector));
  return el;
}

export function findAncestorOrSelf(el, selector) {
  do {
    if (el.matches(selector)) {
      return true;
    }
  } while ((el = el.parentElement));
  return null;
}

export function isVisible(elem) {
  return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
}

// Get absolute offset (top, left) of a node
export function offset(el) {
  // si le noeud est dans un document situé dans une iframe,
  // on ajout l'offet de l'iframe
  let iframeLeft = 0,
    iframeTop = 0;

  if (el.ownerDocument != document) {
    const parentIframe = el.ownerDocument.defaultView.frameElement;
    const iframeOffset = offset(parentIframe);
    iframeLeft = iframeOffset.left;
    iframeTop = iframeOffset.top;
  }

  const rect = el.getBoundingClientRect();
  const scrollLeft = parseInt(window.pageXOffset || document.documentElement.scrollLeft);
  const scrollTop = parseInt(window.pageYOffset || document.documentElement.scrollTop);

  return {
    top: parseInt(rect.top) + scrollTop + iframeTop,
    left: parseInt(rect.left) + scrollLeft + iframeLeft,
    right: parseInt(rect.right) + scrollLeft + iframeLeft,
    bottom: parseInt(rect.bottom) + scrollTop + iframeTop,
  };
}

export function fadeIn(element, duration, callback) {
  if (element) {
    element.style.opacity = 0;

    let last = +new Date();
    let opacity = 0;

    let tick = function () {
      opacity = +element.style.opacity + (new Date().getTime() - last) / duration;
      element.style.opacity = opacity;
      last = +new Date();

      if (+opacity < 1) {
        (window.requestAnimationFrame && requestAnimationFrame(tick)) || setTimeout(tick, 16);
      } else {
        callback && callback();
      }
    };

    tick();
  }
}

export function fadeOut(element, duration, callback) {
  if (element) {
    element.style.opacity = 1;

    let last = +new Date();
    let opacity = 1;

    let tick = function () {
      opacity = +element.style.opacity - (new Date().getTime() - last) / duration;
      element.style.opacity = opacity;
      last = +new Date();

      if (+opacity > 0) {
        (window.requestAnimationFrame && requestAnimationFrame(tick)) || setTimeout(tick, 16);
      } else {
        callback && callback();
      }
    };

    tick();
  }
}

export function getFocusableNodes(node) {
  const nodes = node.querySelectorAll(FOCUSABLE_ELEMENTS);
  return Array(...nodes);
}

/// Retain focus in containerNode
export function retainFocus(event, containerNode) {
  let focusableNodes = getFocusableNodes(containerNode);

  if (focusableNodes.length === 0) return;

  focusableNodes = focusableNodes.filter((node) => {
    return node.offsetParent !== null;
  });

  // if disableFocus is true
  if (!containerNode.contains(document.activeElement)) {
    focusableNodes[0].focus();
  } else {
    const focusedItemIndex = focusableNodes.indexOf(document.activeElement);

    if (event.shiftKey && focusedItemIndex === 0) {
      focusableNodes[focusableNodes.length - 1].focus();
      event.preventDefault();
    }

    if (!event.shiftKey && focusableNodes.length > 0 && focusedItemIndex === focusableNodes.length - 1) {
      focusableNodes[0].focus();
      event.preventDefault();
    }
  }
}

export const getRangeBeforeCursor = (node) => getRangeFromCursor(node, true);
export const getRangeAfterCursor = (node) => getRangeFromCursor(node, false);
export const getRangeFromCursor = (node, start = false) => {
  var range = window.getSelection().getRangeAt(0);
  var clonedRange = range.cloneRange();
  clonedRange.collapse(start);

  // Create a new range from the end of the cloned range to the end of the content editable element
  var newRange = document.createRange();
  newRange.selectNodeContents(node);
  start ? newRange.setEnd(clonedRange.startContainer, clonedRange.startOffset) : newRange.setStart(clonedRange.endContainer, clonedRange.endOffset);

  return newRange;
};

export const getCursorPosition = () => {
  const selection = window.getSelection();
  const range = selection.getRangeAt(0);
  let rect = range.getBoundingClientRect();

  if (range.collapsed && rect.top === 0 && rect.left === 0) {
    let tmpNode = document.createTextNode("\ufeff");
    range.insertNode(tmpNode);
    rect = range.getBoundingClientRect();
    tmpNode.remove();
  }

  return {
    left: parseFloat(rect.left),
    top: parseFloat(rect.top),
  };
};

export const setCursorPosition = (node, x, y) => {
  var range = null;
  if (document.caretPositionFromPoint) {
    var position = document.caretPositionFromPoint(x, y);
    range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
  } else if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(x, y);
  }

  // Set the selection to the new range
  var selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);

  // Set focus to the content editable element
  // node.focus();
};

export const isEmptyNode = (node) => (node ? getNodeContentLength(node) == 0 : true);
export const getNodeContentLength = (node) => node.textContent.length;

/// getCursorOffset within a contenteditable div
export const getCursorOffset = (node) => {
  if (!node) {
    return -1;
  }

  var caretOffset = 0;
  if (typeof window.getSelection != "undefined") {
    var range = window.getSelection().getRangeAt(0);
    var preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(node);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    caretOffset = preCaretRange.toString().length;
  } else if (typeof document.selection != "undefined" && document.selection.type != "Control") {
    var textRange = document.selection.createRange();
    var preCaretTextRange = document.body.createTextRange();
    preCaretTextRange.moveToElementText(node);
    preCaretTextRange.setEndPoint("EndToEnd", textRange);
    caretOffset = preCaretTextRange.text.length;
  }
  return caretOffset;
};

export const setCursorOffset = (node, offset) => {
  const range = document.createRange();
  const sel = window.getSelection();
  return __setCursorOffset(node, { sel, range, offset, currentOffset: 0 }, node);
};

const __setCursorOffset = (node, context, containerNode) => {
  const { range, sel } = context;

  if (node.nodeType === Node.TEXT_NODE) {
    const nodeLength = node.length;
    if (context.currentOffset + nodeLength >= context.offset) {
      range.setStart(node, context.offset - context.currentOffset);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      containerNode.focus();
      return true;
    }
    context.currentOffset += nodeLength;

    /// If node is empty without text node, select it
  } else if (node.childNodes.length == 0) {
    range.setStart(node, 0);
    range.setEnd(node, 0);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } else {
    for (let i = 0; i < node.childNodes.length; i++) {
      if (__setCursorOffset(node.childNodes[i], context, containerNode)) {
        return true;
      }
    }
  }
  return false;
};

export const moveCursorToStart = (node) => moveCursorTo(node, true);
export const moveCursorToEnd = (node) => moveCursorTo(node, false);
const moveCursorTo = (node, start) => {
  const range = document.createRange();
  range.selectNodeContents(node);
  range.collapse(start);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};

/// Return a rect of all absolute position removing padding
export const getNodeContentRect = (node) => {
  const styles = window.getComputedStyle(node, null);

  const prop = (name) => parseFloat(styles.getPropertyValue(name));

  const extraBottom = prop("padding-bottom") + prop("border-bottom-width");
  const extraLeft = prop("padding-left") + prop("border-left-width");
  const extraRight = prop("padding-right") + prop("border-right-width");
  const extraTop = prop("padding-top") + prop("border-top-width");

  const rect = node.getBoundingClientRect();
  return {
    top: rect.top + extraTop,
    left: rect.left + extraLeft,
    right: rect.right - extraRight,
    bottom: rect.bottom - extraBottom,
  };
};

/// Return the line height of the node. If no
export const getLineHeight = (node) => {
  const computedStyle = getComputedStyle(node);
  const lineHeight = computedStyle.getPropertyValue("line-height");
  if (["normal", "initial", "inherit"].includes(lineHeight)) {
    return parseFloat(computedStyle.getPropertyValue("font-size"));
  }
  return parseFloat(lineHeight);
};

export function pasteHtmlAtSelection(html) {
  const selection = window.getSelection();

  let range = selection.getRangeAt(0);
  range.deleteContents();

  // Range.createContextualFragment() would be useful here but is
  // non-standard and not supported in all browsers (IE9, for one)
  const el = document.createElement("div");
  el.innerHTML = html;
  let frag = document.createDocumentFragment(),
    node,
    lastNode;
  while ((node = el.firstChild)) {
    lastNode = frag.appendChild(node);
  }
  range.insertNode(frag);

  // Preserve the selection
  if (lastNode) {
    range = range.cloneRange();
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

export function removeElementsByTagName(node, names) {
  if (!node) {
    return;
  }
  if (!Array.isArray(names)) {
    names = [names];
  }

  if (node.nodeType === Node.ELEMENT_NODE && names.find((name) => node.nodeName === name.toUpperCase())) {
    node.replaceWith(...node.childNodes);
  } else {
    const children = node.childNodes;
    for (let i = 0; i < children.length; i++) {
      removeElementsByTagName(children[i], names);
    }
  }
}

export const findDomParent = (node, filter) => findDomOrSelfParent(node.parentNode, filter);
export function findDomOrSelfParent(node, filter) {
  if (filter(node)) {
    return node;
  }
  return node.parentNode ? findDomOrSelfParent(node.parentNode, filter) : null;
}

export const getRootParent = (node) => findDomParent(node, (n) => !n.parentNode);

export const findNodeIndex = (node) => {
  let index = 0;
  while (node.previousSibling) {
    index++;
    node = node.previousSibling;
  }
  return index;
};

// Helper function to get the XPath of an element
export function getElementXPath(element) {
  const nodes = [];
  while (element && element.contentEditable != "true" && element.parentNode.nodeType === Node.ELEMENT_NODE) {
    const index = findNodeIndex(element);
    nodes.unshift(`[${index}]`);
    element = element.parentNode;
  }
  return nodes.length ? `/${nodes.join("/")}` : null;
}

// Helper function to get an element by XPath
export function getElementByXPath(path, startingNode) {
  const steps = path.split("/");
  let node = startingNode ?? document;
  for (const step of steps) {
    if (step) {
      const match = step.match(/^\[(\d+)\]$/);
      if (match) {
        node = node.childNodes[parseInt(match[1])];
      }
    }
  }
  return node;
}
